import { gql } from '@apollo/client';
import { sortBy, uniqBy } from 'lodash';
import { Highlighter, Typeahead } from 'react-bootstrap-typeahead';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import {
  useDataTransforms,
  useKeyPress,
  useQueryWithMsg,
} from '../../utils/hooks';
import { asRelativeTime } from '../../utils/formatters';
import { captureEvent } from '../../utils/analytics';
import SearchIcon from '../icons/SearchIcon';
import SectionsIcon from '../icons/SectionsIcon';
import { alphanumericSort, augmentFarmTags } from '../../utils/utils';
import {
  getBestMatchForHighlight,
  getNGrams,
  searchBy,
} from '../../utils/search';
import { GreyPill } from '../form-components/RoundedGreyContainer';
import useNavigateToFarm from '../../hooks/useNavigateToFarm';

const SearchTypeahead = styled(Typeahead)`
  .rbt-menu {
    min-width: fit-content;
  }
  .dropdown-item {
    padding: 0.5rem 1rem;
    &:hover {
      background-color: var(--active-color);
    }
  }
  .rbt-input {
    width: 400px;
    padding-left: 2rem;
    @media (max-width: 768px) {
      max-width: calc(100vw - 2 * var(--content-x-padding));
  }
`;

const FarmIcon = styled(SectionsIcon)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--overyield-orange);
`;

const ContainerWithGap = styled.div`
  gap: 4px;
`;

/* This query could get unwieldy if there are too many farms for one user,
but should be ok for now. At some point we may want to do the search serverside. */
const AllFarmsQuery = gql`
  fragment FarmData on Farm {
    id
    name
    updatedAt
    isArchived
    isLocked
    tags
    team {
      name
    }
  }
  query AllUserFarms {
    me {
      id
      ownedFarms {
        ...FarmData
      }
      sharedFarms {
        ...FarmData
      }
      teams {
        id
        farms {
          ...FarmData
        }
      }
    }
  }
`;

const extractAllFarms = (data) => {
  const { ownedFarms, sharedFarms, teams } = data?.me || {};
  const allFarms = [
    ...ownedFarms,
    ...sharedFarms,
    ...teams.flatMap(({ farms }) => farms),
  ];
  // add locked and/or archived to the tags for each farm
  const augmentedFarms = allFarms.map((farm) => augmentFarmTags(farm));
  const uniqueFarms = uniqBy(augmentedFarms, 'id');
  return sortBy(
    [...uniqueFarms].sort((a, b) => alphanumericSort(a.name, b.name)),
    'isArchived',
    'desc',
  );
};

const renderMenuItemChildren = (
  { name, updatedAt, team, id, tags },
  { text },
) => {
  const allNGrams = getNGrams(text);
  const getBestMatch = (stringToMatch) =>
    getBestMatchForHighlight(allNGrams)(stringToMatch);
  return (
    <div className="d-flex align-items-center">
      <FarmIcon />
      <div className="ml-3 font-small">
        <Highlighter search={getBestMatch(name)}>{name}</Highlighter>
        <ContainerWithGap className="d-flex align-items-center">
          <div className="font-tiny">
            <span>Edited {asRelativeTime(updatedAt)}</span>
            {team && (
              <Highlighter
                search={getBestMatch(team.name)}
              >{` · ${team.name}`}</Highlighter>
            )}
          </div>
          {tags?.length > 0 && <span>·</span>}
          {tags?.map((tag) => (
            <GreyPill
              key={`${id}-${tag}`}
              title={tag}
              background={(tag === 'Locked' || tag === 'Archived') && '#d6d6d6'}
            >
              <Highlighter search={getBestMatch(tag)}>{tag}</Highlighter>
            </GreyPill>
          ))}
        </ContainerWithGap>
      </div>
    </div>
  );
};

const FarmSearch = () => {
  const navigateToFarm = useNavigateToFarm();
  const { data, msg } = useQueryWithMsg(AllFarmsQuery);
  const [allFarms = []] = useDataTransforms(data, [extractAllFarms]);
  const { pathname } = useLocation();
  const isMetaKeyPressed = useKeyPress('Meta');
  const isCtrlKeyPressed = useKeyPress('Control');

  return (
    <>
      <SearchTypeahead
        size="sm"
        id="farm-search"
        labelKey="name"
        options={allFarms}
        filterBy={(option, props) => {
          return searchBy(['name', 'team.name', 'tags'])(props.text)(option);
        }}
        placeholder={msg || 'Search all farms'}
        renderMenuItemChildren={renderMenuItemChildren}
        onChange={(selected) => {
          if (selected.length > 0) {
            captureEvent('farm-opened', {
              location: 'farm search',
              inNewTab: isMetaKeyPressed,
            });
            navigateToFarm(
              selected[0].id,
              { state: { source: pathname } },
              isMetaKeyPressed || isCtrlKeyPressed,
            );
          }
        }}
      />
      <SearchIcon
        className="position-absolute ml-2 text-muted"
        width={16}
        height={16}
      />
    </>
  );
};

export default FarmSearch;
